import { Card, Stack, Table, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PageTitle, QueryState } from '../../ui/components';
import { formatDateTime } from '../../ui/format';
import { useWorkplace } from '../access';
import { unwrap, useDb } from '../supabase';
import { AdminOnly } from './AdminOnly';

/** A4 Content (Admin): RISQ versions and the import log. Imports run from the CLI / workflow. */
export function AdminContentPage() {
  const { t } = useTranslation('dashboard');
  const db = useDb();
  const { access } = useWorkplace();
  const data = useQuery({
    queryKey: ['admin', 'content'],
    enabled: access.isAdmin,
    queryFn: async () => ({
      versions: unwrap(
        await db
          .from('risq_versions')
          .select('id, code, title, is_current, imported_at')
          .is('deleted_at', null)
          .order('code'),
      ),
      runs: unwrap(
        await db
          .from('risq_import_runs')
          .select('id, version_id, company_id, source_file, changes, created_at')
          .order('created_at', { ascending: false })
          .limit(50),
      ),
    }),
  });
  const version = (id: string) => data.data?.versions.find((v) => v.id === id)?.code ?? '';
  return (
    <AdminOnly>
      <PageTitle title={t('admin.contentTitle')} />
      <QueryState loading={data.isPending} error={data.error}>
        {() => (
          <Stack gap="lg">
            <Card withBorder>
              <Stack>
                <Title order={2} size="h3">
                  {t('admin.versions')}
                </Title>
                <Table>
                  <Table.Tbody>
                    {(data.data?.versions ?? []).map((v) => (
                      <Table.Tr key={v.id}>
                        <Table.Td fw={600}>{v.code}</Table.Td>
                        <Table.Td>{v.title}</Table.Td>
                        <Table.Td>
                          {v.is_current ? t('admin.current') : t('admin.notCurrent')}
                        </Table.Td>
                        <Table.Td>
                          {v.imported_at === null ? '' : formatDateTime(v.imported_at)}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Stack>
            </Card>
            <Card withBorder>
              <Stack>
                <Title order={2} size="h3">
                  {t('admin.imports')}
                </Title>
                {(data.data?.runs ?? []).length === 0 ? <Text>{t('admin.noImports')}</Text> : null}
                <Table>
                  <Table.Tbody>
                    {(data.data?.runs ?? []).map((r) => (
                      <Table.Tr key={r.id}>
                        <Table.Td>{formatDateTime(r.created_at)}</Table.Td>
                        <Table.Td>
                          {t('admin.versionCode', { code: version(r.version_id) })}
                        </Table.Td>
                        <Table.Td>{r.source_file}</Table.Td>
                        <Table.Td>
                          <Text size="xs" ff="monospace">
                            {JSON.stringify(r.changes)}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Stack>
            </Card>
          </Stack>
        )}
      </QueryState>
    </AdminOnly>
  );
}
